<script lang="ts" setup>
import AUApi from "@/api";
import { ref, onMounted, reactive, nextTick } from "vue";
import { isPhone } from "@/DeviceInfoPlugin";
import {
  getMonthNames,
  getWeekdayNamesShort,
  valueToFormatStr,
} from "@/editor/CalenderHelper";
import { Globals } from "@/Globals";
import {
  globalsBezeichnung,
  globalsKennung,
  globalsBegriffMemo,
  checkBegriff,
} from "@/local";
import Constants from "@/Constants";
import { calcFunctionNew } from "@/CalcHelperStatic";
import baseList from "@/components/baseList.vue";
import {
  getKZValue,
  getKZValueDisplay,
  getKZTitle,
  getKennungValueDisplay,
} from "@/DataHelper";
import {
  mountedBase,
  BaseSetup,
  reloadData,
  onDelete,
  loadAmpel,
} from "@/DataLoaderStatic";

import { DialogParameter, showDialog, showSnackbar } from "@/UIHelper";
import {
  refreshFullPageContent,
  getSiteData,
  goBack,
} from "@/NavigationHelper";
import {
  TimeLineDayProps,
  TimeLineDayUIData,
  getSperrDateDay,
} from "./TimelineCarouselFunctions";

const props = defineProps<TimeLineDayProps>();
const emit = defineEmits<{
  back: [];
  goToDate: [date?: any];
  calculated: [data?: any];
}>();

const pageParams: DataPageParameter = {
  zeitbereich: AUApi.Current.zeitbereich,
  insertMode: false,
  query: "WebStempelungenSoftTerm",
  queryAIC: AUApi.Current.user.aic,
  aic: AUApi.Current.user.aic,
  emitBack: () => emit("back"),
};
const myheight = innerHeight - 150;
const uiData = reactive<TimeLineDayUIData>(
  BaseSetup<DataPageParameter, TimeLineDayUIData>(pageParams, {
    wochenTag: "",
    monat: "-",
    titleColor: "dvhBackground",
    picker: new Date().toISOStringWithTZ().substr(0, 10),
    warningBtn: "warning",
    isApiBusy: false,
    calcToday: false,
    reisen: false,
    reiseBeginn: false,
    bdeErlaubt: false,
    maxDate: new Date().toISOStringWithTZ().substr(0, 10),
    colorZulagen: "grey",
    items: [],
    heightLocal: props.contentHeight ?? myheight,
    widthLocal: props.contentWidth ?? 300,
    textReise: "",
    name: "",
    sperre: false,
    tag: 0,
    colorManZulagen: "grey",
  }),
);
const menu = ref<boolean>(false);
const datePicker: Date = new Date();
let setCompKey = ref<number>(0);
let setCompKeyFehler = ref<number>(0);
let zusatzTextReise = ref<string>("");
let anzahlReisen = ref<number>(0);
onMounted(async () => {
  uiData.isApiBusy = AUApi.Current.isBusy;
  uiData.initialized = false;
  uiData.showReisekostenMutable = props.showReisekosten;
  if (checkBegriff("BDEErfassun_Nacherfassung")) {
    uiData.bdeErlaubt = true;
  }
  if (checkBegriff("Web_NacherfassungBDE") && !props.admin) {
    uiData.bdeAntrag = true;
  }
  if (
    checkBegriff("Web_NacherfassungSoftTerm") ||
    checkBegriff("Web_NacherfassungRK")
  ) {
    if (!checkBegriff("Web_NacherfassungAntragSoftTerm")) {
      uiData.showEditDelete = true;
    }
  }
  if (props.admin) {
    uiData.sperre = false;
    uiData.showEditDelete = true;
    uiData.showBtnNacherfassung = true;
    // gibt es eine Vorgesetzten Periodensperre dann soll die bei der ADMIN Sicht greifen!
    const localSperrDate = getSperrDateDay(pageParams, props.admin);
    if (localSperrDate && props.day) {
      if (props.day < localSperrDate) {
        uiData.sperre = true;
      }
    }
  }
  if (
    checkBegriff("Web_NacherfassungSoftTerm") ||
    checkBegriff("Web_NacherfassungAntragSoftTerm") ||
    checkBegriff("Web_NacherfassungRK")
  ) {
    uiData.showBtnNacherfassung = true;
  }
  const days = getWeekdayNamesShort();
  const siteData = getSiteData();
  let dayLocal = siteData?.day ?? props.day;
  if (!dayLocal) {
    dayLocal = AUApi.Current.zeitbereich.von;
  }
  uiData.picker = dayLocal.toISOStringWithTZ().substr(0, 10);
  if (props.nameUebergabe) {
    uiData.name = props.nameUebergabe;
  }
  const d = dayLocal;
  uiData.wochenTag = days[d.getDay()];
  uiData.tag = dayLocal.getDate();
  uiData.monat = getMonthNames()[dayLocal.getMonth()];
  if (d) {
    const holidayLocal = AUApi.Current.getHoliday(d);
    if (holidayLocal) {
      uiData.holiday = holidayLocal;
      uiData.titleColor = "redBackground";
    }
  }
  pageParams.zeitbereich = uiData.editZeitbereich = {
    von: dayLocal,
    bis: dayLocal,
    bereich: "Tag",
  };
  if (!uiData.editZeitbereich) {
    return;
  }
  // auf tag eingrenzen
  uiData.editZeitbereich = {
    von: uiData.editZeitbereich.von,
    bis: uiData.editZeitbereich.von,
    bereich: "Tag",
  };
  if (props.aic) {
    // gibt es übergabe AIC - dann zieht der vor!
    pageParams.queryAIC = props.aic;
    pageParams.aic = props.aic;
  }
  if (checkBegriff("WEB_heute_Berechnen")) {
    uiData.calcToday = true;
  }
  uiData.reisen = checkBegriff("Web_Reisekosten");
  await mountedBase(uiData, pageParams, false);

  if (!uiData.showLoading && uiData.data) {
    await refreshItems();
  }

  uiData.colorZulagen = "grey";
  if (props.showName && checkBegriff("Web_Workflow_StatusTimerSatz")) {
    loadAmpel(
      uiData,
      pageParams,
      "Web_Workflow_StatusTimerSatz",
      false,
      false,
      0,
      true,
    );
  } else {
    loadAmpel(uiData, pageParams, "Web_StatusAntrag", true);
  }
  if (props.showManZulagen) {
    await zulagenLoaded();
  }
  uiData.showLoading = false;
  uiData.initialized = true;
});
function dataLoaded(data: any) {
  uiData.lastRefresh = valueToFormatStr(new Date(), "dd.MM.yy HH:mm:ss");

  dataLoaded(data);
}
async function zulagenLoaded() {
  const zulagenQuery = "Web_Uebersicht_Manuelle_Zulagenerfassung";
  AUApi.Current.getQuery(
    zulagenQuery,
    (data: any) => dataLoadedZulagen(data),
    pageParams.queryAIC,
    uiData.editZeitbereich,
    0,
    false,
    false,
    "",
    0,
  );
}
function dataLoadedZulagen(data: any) {
  if (data.data.error !== undefined) {
    AUApi.Current.onfail(data.data.error);
    console.log(data.data.error);
    alert("ACHTUNG: " + data.data.error);
    emit("back");
    return;
  }
  const datenZulagen = data.data.data;
  if (datenZulagen.length > 0) {
    uiData.colorManZulagen = "green";
  } else {
    uiData.colorManZulagen = "grey";
  }
}
async function refreshItems() {
  setCompKey.value += +1;
  setCompKeyFehler.value += +1;
  uiData.reiseBeginn = false;

  uiData.items = []; // ausleeren für refresh
  if (uiData.data.length > 0) {
    // uiData.textReise = "";
    uiData.timeLine = true;
  }
  for (let i = 0; i < uiData.data.length; i++) {
    const row = uiData.data[i];
    let dot = "mdi-dot";
    const antrag = getKZValueDisplay(row, "Antrag", uiData.columns);
    let avatar = getKZValueDisplay(row, "avatar", uiData.columns);
    const header = getKZValueDisplay(row, "header", uiData.columns);
    const title = getKZValueDisplay(row, "titel", uiData.columns);
    const title2 = getKZValueDisplay(row, "titel2", uiData.columns);
    const auftrag = getKZValueDisplay(row, "Auftrag", uiData.columns);
    const textAuftrag = getKZTitle(row, "Auftrag", uiData.columns);
    const kostenstelle = getKZValueDisplay(row, "Kst", uiData.columns);
    const textKst = getKZTitle(row, "Kst", uiData.columns);
    const taetigkeit = getKZValueDisplay(row, "Taetigkeit", uiData.columns);
    const textTaetigkeit = getKZTitle(row, "Taetigkeit", uiData.columns);
    const subtitel = getKZValueDisplay(row, "subtitel", uiData.columns);
    const subtitel1 = getKZValueDisplay(row, "subtitel1", uiData.columns);
    const text = getKZValueDisplay(row, "text", uiData.columns);
    const icon = getKZValueDisplay(row, "icon", uiData.columns);
    const tagessperre = getKZValueDisplay(row, "sperre", uiData.columns);
    // gibt es nach einer Zeile die den Tag sperrt noch eine andere zeile - dann hebt diese die Sperre auf!
    if (tagessperre && !props.admin) {
      // admin darf immer
      uiData.sperre = true;
    }
    let color = getKZValueDisplay(row, "color", uiData.columns);
    const aicAntrag = getKZValue(row, "AICAntrag", uiData.columns);
    const aicReise = getKZValue(row, "AICReise", uiData.columns);
    const bde = getKZValueDisplay(row, "BDE", uiData.columns);
    const reise = getKZValueDisplay(row, "reise", uiData.columns);
    const noDelete = getKZValue(row, "noDelete", uiData.columns);
    const sperreLocal = getKZValue(row, "sperreReise", uiData.columns);
    const gps = getKZValue(row, "gps", uiData.columns);
    const bdeErlaubt = checkBegriff("BDEErfassun_Nacherfassung");
    if (sperreLocal && !props.admin) {
      // admin darf immer
      uiData.sperre = sperreLocal;
    }
    const reiseName = getKennungValueDisplay(
      row,
      "RK_REISEBEWSTAMM",
      uiData.columns,
    );
    if (reiseName) {
      if (uiData.textReise && uiData.textReise !== reiseName) {
        // uiData.textReise = uiData.textReise + " +1";
      } else {
        uiData.textReise = reiseName;
      }
    } else if (!reise && uiData.textReise) {
      uiData.textReise = "";
    }
    const bewilligtAntrag = getKZValueDisplay(row, "bewilligt", uiData.columns);
    const abgelehntAntrag = getKZValueDisplay(row, "abgelehnt", uiData.columns);
    const delDatensatz = getKZValueDisplay(row, "delete", uiData.columns);
    if (uiData.name === "" && props.showName) {
      uiData.name = getKZValueDisplay(row, "name", uiData.columns);
    }
    if (reise === "reise") {
      dot = "local_airport";
      uiData.reiseBeginn = true;
    }
    if (bde) {
      dot = "extension";
      color = "black";
    }
    if (antrag === "1.00" || antrag === "1,00") {
      dot = "mdi-star";
    }
    if (delDatensatz === "1.0") {
      dot = "delete_forever";
      // color = "red";
    }
    if (!color) {
      color = "green";
    }
    if (avatar) {
      const start = avatar.indexOf('"') + 1;
      const len = avatar.lastIndexOf('"') - start;
      avatar = avatar.substr(start, len);
    } else if (!avatar) {
      avatar = "mdi-account-circle";
    }
    uiData.items.push({
      header,
      title,
      title2,
      subtitel,
      subtitel1,
      auftrag,
      textAuftrag,
      kostenstelle,
      textKst,
      taetigkeit,
      textTaetigkeit,
      text,
      avatar,
      icon,
      divider: true,
      inset: true,
      color,
      aic: row.aic_Bew_pool,
      menu: false,
      dot,
      aicAntrag,
      bewilligtAntrag,
      abgelehntAntrag,
      bde,
      reise,
      reiseName,
      aicReise,
      noDelete,
      sperre: uiData.sperre, // warum nehmen wir das in jedes item auf, wenns eh global is ?
      antrag,
      Abflatlong: gps,
    });
  }
  uiData.timeLine = true;
  uiData.initialized = true;
}
async function bewDelete(aicLocal: number | undefined) {
  if (aicLocal) {
    if (uiData.editZeitbereich) {
      // für HEUTE soll die Zeitbuchung NICHT ausgelöst werden
      const d = new Date();
      const datumHeute = new Date(d.setHours(0, 0, 0, 0));
      const vonZeitZone = uiData.editZeitbereich.von;
      const von = new Date(vonZeitZone.setHours(0, 0, 0, 0));
      if (
        uiData.calcToday &&
        datumHeute.valueOf() === von.valueOf() &&
        checkBegriff("Web_NacherfassungSoftTerm")
      ) {
        // nicht das Modell:  "ZE_IstZeit_SoftTerm_berechnen"
        // sondern immer Zeit - Zulagenbuchung
        // 7.7.2021
        await onDelete(
          uiData,
          pageParams,
          aicLocal,
          "Web_Zeit_Zulagenbuchung",
          uiData.editZeitbereich,
        );
      } else if (
        datumHeute.valueOf() !== von.valueOf() &&
        checkBegriff("Web_NacherfassungSoftTerm")
      ) {
        await onDelete(
          uiData,
          pageParams,
          aicLocal,
          "Web_Zeit_Zulagenbuchung",
          uiData.editZeitbereich,
        );
      } else {
        await onDelete(
          uiData,
          pageParams,
          aicLocal,
          "",
          uiData.editZeitbereich,
        );
      }
    }
  }
  uiData.initialized = false;
  onDialogClose();
}
async function bewDeleteAntrag(
  aicAntrag?: any,
  aicLocal?: any,
  useAic?: boolean,
  antrag?: string,
  item?: any,
) {
  // wenn es eine Stemplung mit Reise ist - dann aic zum Löschen
  // wenn es Antrag ist immer AICAntrag
  // wenn es nur eine ReiseBuchung ist AICAntrag
  uiData.timeLine = false;
  if (aicAntrag && (!useAic || antrag === "1,00")) {
    await onDelete(uiData, pageParams, aicAntrag, "", uiData.editZeitbereich);
  } else if (aicLocal === 0) {
    await onDelete(uiData, pageParams, aicAntrag, "", uiData.editZeitbereich);
  } else {
    await onDelete(uiData, pageParams, aicLocal, "", uiData.editZeitbereich);
    setTimeout(() => refreshFullPageContent());
    // beim Löschen von ReiseDaten muss ich immer alles neu Laden! damit die Reisen sauber angezeigt werden können
  }
  uiData.initialized = false;
  await onDialogClose(item);
}
function warningListLoaded(args?: any) {
  uiData.warningBtnVisible = false;
  if (props.showError) {
    // wenn es mindestens ein item gibt dann zeigen wirs an

    if (args && args.data && args.data.length > 0) {
      uiData.warningBtn = "warning";
      uiData.warningBtnVisible = true;
      const warningColumns = args.columns;
      for (let i = 0; i < args.data.length; i++) {
        const row = args.data[i];
        let icon = getKZValueDisplay(row, "icon", warningColumns);
        if (icon === "mdi-alert-circle-outline") {
          uiData.warningBtn = "error";
          return;
        }
      }
    }
  }
}
function onBack(item: any) {
  // vom BDE Edit kommt er hier her - wurden daten gespeichert dann kommt ITEM mit
  // wird nicht gespeichert, brauch ich auch nix rechnen!
  if (item) {
    item.menu = false;
  }
}
async function onDialogClose(item?: any) {
  await reloadData(uiData, pageParams);
  await refreshItems();
  if (item && checkBegriff("Web_NacherfassungSoftTerm")) {
    item.menu = false;
    setTimeout(() => calcModell("Web_Zeit_Zulagenbuchung"), 20);
  }
}

async function calcModell(
  modellUebergabe: string,
  abwZeitraum?: string,
  fullReload?: boolean,
) {
  const modell = modellUebergabe;
  if (uiData.editZeitbereich) {
    // für HEUTE soll die Zeitbuchung NICHT ausgelöst werden
    const d = new Date();
    const datumHeute = new Date(d.setHours(0, 0, 0, 0));
    const vonZeitZone = uiData.editZeitbereich.von;
    const von = new Date(vonZeitZone.setHours(0, 0, 0, 0));
    const begriffModell = globalsKennung(Globals.Begriff, modell);
    if (uiData.calcToday || datumHeute.valueOf() !== von.valueOf()) {
      if (abwZeitraum) {
        const dayLocal = new Date(abwZeitraum);
        if (dayLocal) {
          uiData.tempZeitbereich = uiData.editZeitbereich;
          uiData.editZeitbereich = {
            von: dayLocal,
            bis: dayLocal,
            bereich: "Tag",
          };
        }
      }
      if (begriffModell) {
        await calcFunctionNew(
          modell,
          props.aic ?? 0,
          false,
          (data) => {
            console.log("CalcButton calculated");
            emit("calculated", data);
          },
          uiData.editZeitbereich,
          [],
        )
          ?.then(() => {
            if (uiData.tempZeitbereich) {
              uiData.editZeitbereich = uiData.tempZeitbereich;
              uiData.tempZeitbereich = undefined;
              onDialogClose();
            } else {
              onDialogClose();
              if (fullReload) {
                setTimeout(() => refreshFullPageContent());
              }
            }
          })
          .catch((error: any) => {
            console.log(error);
          });
        // den Teil muss ich drinnen lassen, weil sonst wird erst der Relaod
        // gemacht wenn die Zeitbuchung fertig ist! wir warten aber nicht drauf!
        if (uiData.tempZeitbereich) {
          uiData.editZeitbereich = uiData.tempZeitbereich;
          uiData.tempZeitbereich = undefined;
          onDialogClose();
        } else {
          onDialogClose();
          if (fullReload) {
            setTimeout(() => refreshFullPageContent());
          }
        }
        return;
      } else {
        onDialogClose();
        return;
      }
    }
    onDialogClose();
    return;
  }
}
function updateReiseName(
  name: string,
  beginnReise: string,
  endeReise: string,
  anzahlReisenLocal: number,
) {
  anzahlReisen.value = anzahlReisenLocal;
  uiData.textReise = name;
  if (endeReise) {
    zusatzTextReise.value = beginnReise + " - " + endeReise;
  } else {
    zusatzTextReise.value = beginnReise;
  }
}
function openProjektstunden() {
  if (!props.day) {
    return;
  }
  let aic = 0;
  if (props.showName) {
    // showName ist aktiv wenn VG diese Sicht hat
    aic = props.aic ?? 0;
  }
  let showFullscreen = false;
  console.log("isPhone: " + isPhone());
  if (Constants.isMobile && isPhone()) {
    showFullscreen = true;
  }
  showDialog({
    title: globalsBezeichnung(
      Globals.Begriff,
      "WebWFProjektstunden",
      "Projektstunden",
    ),
    width: 400,
    fullscreen: showFullscreen,
    site: "SoftTermProjektstundenProjektstunden",
    data: { zeitbereich: uiData.editZeitbereich, aic, admin: props.admin },
    onClose: (dlg: DialogParameter) => {
      setCompKey.value += +1;
      return true;
    },
  });
}
function openmanZulagen() {
  if (!props.day) {
    return;
  }
  let showFullscreen = false;
  console.log("isPhone: " + isPhone());
  if (Constants.isMobile && isPhone()) {
    showFullscreen = true;
  }
  let aic = 0;
  if (props.showName) {
    // showName ist aktiv wenn VG diese Sicht hat
    aic = props.aic ?? 0;
  }
  const query = "Web_Uebersicht_Manuelle_Zulagenerfassung";
  showDialog({
    title: globalsBezeichnung(
      Globals.Begriff,
      "Web_ErfassungUeberstundenZulagen",
      "Erfassung Überstunden/Zulagen",
    ),
    width: 400,
    fullscreen: showFullscreen,
    site: "SoftTermProjektstundenUebersicht",
    data: {
      zeitbereich: uiData.editZeitbereich,
      query,
      zulagen: true,
      day: props.day,
      aic,
    },
    onClose: (dlg: DialogParameter) => {
      zulagenLoaded();

      setCompKey.value += +1;
      return true;
    },
  });
}
function openNacherfassung() {
  // solange die Zeitncuhung nicht fertig ist - soll man nicht NEU Erfassen können!
  uiData.isApiBusy = AUApi.Current.isBusy;
  if (uiData.isApiBusy) {
    showSnackbar({
      text: globalsBezeichnung(
        Globals.Begriff,
        "Web_Berechnung_laueft:",
        "Berechnung läuft...",
      ),
      duration: 800,
      color: "error",
    });
    return;
  }

  let showFullscreen = false;
  if (Constants.isMobile) {
    showFullscreen = true;
  }
  let onlyBDE = false;
  if (uiData.sperre && uiData.bdeErlaubt) {
    onlyBDE = true;
  }
  let aic = 0;
  if (props.showName) {
    // showName ist aktiv wenn VG diese Sicht hat
    aic = props.aic ?? 0;
  }

  showDialog({
    title: globalsBezeichnung(
      Globals.Begriff,
      "Web_Nacherfassung",
      "Nacherfassung",
    ),
    site: "SoftTermNacherfassung",
    width: 420,
    fullscreen: showFullscreen,
    data: {
      zeitbereich: uiData.editZeitbereich,
      reisenBeginn: uiData.reiseBeginn,
      nurBDE: onlyBDE,
      aic,
      admin: props.admin,
    },
    onClose: (dlg: DialogParameter) => {
      if (dlg.data.abwZeitraum || dlg.data.fullReload) {
        const abwZeitraum = dlg.data.abwZeitraum;
        const fullReload = dlg.data.fullReload;
        uiData.timeLine = false;
        calcModell("Web_ZeitBuchungPruefung", abwZeitraum, fullReload);
      } else {
        uiData.timeLine = false;
        calcModell("Web_ZeitBuchungPruefung");
      }
      return true;
    },
  });
}
/* Calc function für neuberechnung  */
function calcFunction(
  // brauch ich für den Antrag!
  modell: string,
  aicLocal: number,
  fromDialog: boolean = false,
  fromBew: boolean = false,
) {
  let begriff: any;
  if (modell) {
    begriff = globalsKennung(Globals.Begriff, modell);
  }
  if (begriff.userFrage && fromDialog !== true) {
    const userFrage = globalsBegriffMemo(
      Globals.Begriff,
      begriff.userFrage,
      "Soll die Berechnung durchgeführt werden?",
    );
    showDialog({
      title: "Frage",
      width: 300,
      height: 400,
      text: userFrage,
      persistent: true,
      okButton: true,
      onClose: (x: DialogParameter) => {
        if (x.dialogResult === true) {
          calcFunction(modell, aicLocal, true, fromBew);
        }
        return true;
      },
    });
    return Promise.resolve();
  }
  uiData.showLoading = true;
  const varUbergabe: VarUebergabe[] = [];
  const tabUbergabe: number[] = [];
  const vecStamm: number[] = [];
  const bemerkung: string = "Web - TimeLineDay";
  let bewAic = 0;

  if (fromBew === true) {
    bewAic = aicLocal;
    aicLocal = 0;
  }
  AUApi.Current.postCalc(
    varUbergabe,
    tabUbergabe,
    begriff.aic,
    aicLocal,
    uiData.editZeitbereich,
    0,
    0,
    bemerkung,
    success,
    begriff.maxB,
    vecStamm,
    bewAic,
  );
  uiData.showLoading = false;
}
function openTagesSalden() {
  if (!props.day) {
    return;
  }
  let showFullscreen = false;
  if (Constants.isMobile) {
    showFullscreen = true;
  }
  let isVorgesetzter = false;
  let aicLocal = 0;
  if (props.showName) {
    // showName ist aktiv wenn VG diese Sicht hat
    isVorgesetzter = true;
    aicLocal = props.aic ?? 0;
  }
  showDialog({
    title: props.day.toLocaleDateString(),
    site: "SoftTermTagesSalden",
    width: 400,
    fullscreen: showFullscreen,
    data: {
      zeitbereich: uiData.editZeitbereich,
      sperre: uiData.sperre,
      isVorgesetzter,
      aic: aicLocal,
    },
    onClose: (x: DialogParameter) => {
      uiData.colorZulagen = "";
      if (x) {
        if (x.ergebnis) {
          const ergebnis = x.ergebnis;
          if (ergebnis === 0) {
            uiData.colorZulagen = "warning";
          } else if (ergebnis === 1) {
            uiData.colorZulagen = "success";
          } else if (ergebnis === 2) {
            uiData.colorZulagen = "error";
          } else if (ergebnis === 3) {
            uiData.colorZulagen = "error";
          }
        } else {
          if (props.showName && checkBegriff("Web_Workflow_StatusTimerSatz")) {
            loadAmpel(
              uiData,
              pageParams,
              "Web_Workflow_StatusTimerSatz",
              false,
              false,
              0,
              true,
            );
          } else {
            loadAmpel(uiData, pageParams, "Web_StatusAntrag", true);
          }
        }
      } else {
        setTimeout(
          () => loadAmpel(uiData, pageParams, "Web_StatusAntrag", true),
          100,
        );
      }

      setTimeout(
        () => loadAmpel(uiData, pageParams, "Web_StatusAntrag", true),
        100,
      );
      reloadData(uiData, pageParams);
      return true;
    },
  });
}
function openEditStempelungen(aicLocal?: number) {
  if (!props.day) {
    return;
  }
  let showFullscreen = false;
  if (Constants.isMobile && isPhone()) {
    showFullscreen = true;
  }

  showDialog({
    title: props?.day?.toLocaleDateString(),
    site: "SoftTermStempelEdit",
    width: 300,
    fullscreen: showFullscreen,
    data: {
      zeitbereich: uiData.editZeitbereich,
      aic: aicLocal,
    },
    onClose: () => {
      if (checkBegriff("Web_NacherfassungSoftTerm")) {
        // timeLine = false;
        (calcModell("Web_Zeit_Zulagenbuchung"), 20);
      } else if (checkBegriff("Web_NacherfassungRK")) {
        uiData.timeLine = false;
        onDialogClose();
      }
      return true;
    },
  });
}
function openFrame(func: any) {
  func();
}
function openEditStempelungenBDE(aicLocal?: number) {
  if (!props.day) {
    return;
  }
  uiData.isApiBusy = AUApi.Current.isBusy;
  if (uiData.isApiBusy) {
    showSnackbar({
      text: globalsBezeichnung(
        Globals.Begriff,
        "Web_Berechnung_laueft:",
        "Berechnung läuft...",
      ),
      duration: 800,
      color: "error",
    });
    return;
  }
  let showFullscreen = false;
  if (Constants.isMobile && isPhone()) {
    showFullscreen = true;
  }
  showDialog({
    title: props.day.toLocaleDateString(),
    site: "SoftTermStempelEditBde",
    width: 300,
    fullscreen: showFullscreen,
    data: {
      zeitbereich: uiData.editZeitbereich,
      aic: aicLocal,
    },
    onClose: (x: DialogParameter) => {
      if (x?.dialogResult) {
        uiData.timeLine = false;
        setTimeout(() => calcModell("Web_Zeit_Zulagenbuchung"), 20);
      }
      return true;
    },
  });
}
function openEditStempelantrag(
  aicAntrag: number,
  bewilligtAntrag: string | undefined,
  abgelehntAntrag: string | undefined,
  edit: boolean | undefined,
  bde: boolean | undefined,
  openAlways?: boolean,
  reise?: string,
  noDelete?: boolean,
  oriAIC?: number,
) {
  if (!props.day) {
    return;
  }
  uiData.isApiBusy = AUApi.Current.isBusy;
  if (uiData.isApiBusy && !openAlways) {
    showSnackbar({
      text: globalsBezeichnung(
        Globals.Begriff,
        "Web_Berechnung_laueft:",
        "Berechnung läuft...",
      ),
      duration: 800,
      color: "error",
    });
    return;
  }
  let textTitel = globalsBezeichnung(Globals.Begriff, "Web_offen", "offen");
  if (bewilligtAntrag) {
    textTitel = bewilligtAntrag;
  } else if (abgelehntAntrag) {
    textTitel = abgelehntAntrag;
  }
  let width = 600;
  if (window.innerWidth < 600) {
    width = window.innerWidth - 10;
  }
  let showFullscreen = false;
  if (Constants.isMobile) {
    showFullscreen = true;
  }
  const freigabe = props.freigabeAntrag;
  const text = globalsBezeichnung(Globals.Begriff, "Antrag", "Antrag");
  showDialog({
    title: props.day.toLocaleDateString() + " " + text + ": " + textTitel,
    site: "SoftTermEditStempelantrag",
    width,
    fullscreen: showFullscreen,
    persistent: true,
    data: {
      zeitbereich: uiData.editZeitbereich,
      aic: aicAntrag,
      freigabe,
      bewegungsdaten: true,
      edit,
      bde,
      reise,
      noDelete,
      oriAIC,
    },
    onClose: (x: DialogParameter) => {
      uiData.timeLine = false;
      onDialogClose();
      return true;
    },
  });
}
function openEditReise(
  aicReise: number,
  reiseName: string | undefined,
  aic: number | undefined,
  aicAntrag: number | undefined,
) {
  if (!props.day) {
    return;
  }
  let showFullscreen = false;
  if (Constants.isMobile) {
    showFullscreen = true;
  }
  console.log("fullscreen: " + showFullscreen);
  let showZeit = false;
  if (!aic && !aicAntrag) {
    showZeit = true;
    // ist es NUR eine Reisezeit (Grenzübertritt) - dann darf auch die Uhrzeit geändert werden!
  }
  const title = props.day.toLocaleDateString() + ": " + reiseName;

  showDialog({
    title,
    site: "SoftTermReisenEditReisen",
    width: 420,
    persistent: true,
    fullscreen: showFullscreen,
    data: {
      zeitbereich: uiData.editZeitbereich,
      aic: aicReise,
      bewegungsdaten: true,
      showZeit,
    },
    onClose: (x: DialogParameter) => {
      if (x?.returnedData || x?.dialogResult) {
        uiData.timeLine = false;
        onDialogClose();
      }
      return true;
    },
  });
}
function success(res: any) {
  const aicResponse = res.data.aic;
  if (aicResponse) {
    let bde = false;
    if (res.data!.JSON && res.data.JSON.BDE) {
      bde = res.data.JSON.BDE;
    }
    let reise = "";
    if (res.data!.JSON && res.data.JSON.Reise) {
      reise = res.data.JSON.Reise;
    }
    // Antrag muss immer geöffnet werden auch wenn Modell eventuell noch auf Busy ist!
    openEditStempelantrag(
      aicResponse,
      "",
      "",
      false,
      bde,
      true,
      reise,
      false,
      aicResponse,
    );
  }
}
function setDate(value: Date) {
  uiData.dialogDate = false;
  const date = new Date(value);
  if (props.day) {
    uiData.picker = props.day.toISOStringWithTZ().substr(0, 10);
  }
  emit("goToDate", date);
}
function selectToday() {
  const date = new Date();
  setDate(date);
  // uiData.picker = new Date().toISOString().substr(0, 10);
}
</script>
<template>
  <v-card
    :min-height="uiData.heightLocal ?? 300"
    :max-width="450"
    class="pt-1 pl-0 pr-1"
  >
    <v-card-title class="pa-0">
      <v-toolbar
        density="compact"
        :class="uiData.titleColor"
        style="color: white"
      >
        <div class="headline">
          <v-row class="headline d-flex align-center ga-1 ma-0">
            <div class="headline font-weight-light white--text pt-2 ma-0">
              {{ uiData.wochenTag }}
            </div>

            <h2 class="display-2 font-weight-regular white--text ma-0">
              {{ uiData.tag }}
            </h2>
          </v-row>
          <v-row class="ml-1 mt-n2" v-if="uiData.holiday">
            <span style="color: white; font-size: 10px; max-height: 10px"
              >{{ uiData.holiday.bezeichnung }}
            </span>
          </v-row>
        </div>
        <v-menu
          v-if="!props.admin && !showName"
          v-model="uiData.dialogDate"
          :close-on-content-click="false"
          class="pa-0"
        >
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              color="white"
              variant="text"
              class="pt-2 pa-0"
              append-icon="mdi-share"
            >
              {{ uiData.monat }}
            </v-btn>
          </template>
          <v-card min-width="400">
            <div style="background: white">
              <v-date-picker
                locale="de-de"
                first-day-of-week="1"
                :show-week="true"
                :min="props.minDate"
                :max="uiData.maxDate"
                v-model="datePicker"
                @update:model-value="setDate"
              ></v-date-picker>
            </div>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="primary" @click="selectToday" text="Today"></v-btn>
              <!-- <v-btn
                variant="text"
                color="primary"
                @click="setDate()"
                icon="mdi-arrow-right"
              ></v-btn> -->
              <v-btn
                ariant="text"
                color="primary"
                @click="uiData.dialogDate = false"
                icon="mdi-close"
              ></v-btn>
            </v-card-actions>
          </v-card>
        </v-menu>
        <span v-else v-bind="props" variant="tonal">
          {{ uiData.monat }}
        </span>
        <v-spacer></v-spacer>
        <div
          v-if="showName"
          class="pr-2 text-subtitle-1 font-weight-light white--text"
        >
          {{ uiData.name }}
        </div>
        <v-divider v-if="showName" class="px-2" vertical></v-divider>
        <v-btn
          v-if="showName"
          variant="text"
          @click="goBack()"
          icon="mdi-close"
        ></v-btn>
        <v-btn
          :color="uiData.warningBtn"
          v-if="uiData.warningBtnVisible"
          @click="uiData.showDialogLocal = true"
          icon="mdi-alert"
          size="small"
          variant="flat"
        ></v-btn>
        <v-btn
          v-if="
            showNacherfassung &&
            uiData.showBtnNacherfassung &&
            (!uiData.sperre || uiData.bdeErlaubt)
          "
          variant="flat"
          color="info"
          size="small"
          @click.prevent.stop="openNacherfassung()"
          icon="mdi-plus"
        >
        </v-btn>
      </v-toolbar>
      <v-toolbar density="compact" :class="uiData.titleColor">
        <v-btn
          v-bind="props"
          variant="flat"
          v-if="uiData.textReise && showReisekosten"
          @click="menu = !menu"
          class="text-left px-0 mr-2 py-0"
          color="grey"
          rounded="0"
        >
          <span class="text-label-medium">
            {{ uiData.textReise?.slice(0, 30)
            }}<span v-if="uiData.textReise?.length > 30">…</span>
            <br />
            <span class="text-body-small">{{ zusatzTextReise }}</span>
          </span>
          <v-tooltip
            v-if="anzahlReisen > 1"
            text="mehrere Reisen für den gleichen Zeitraum erfasst"
            location="bottom"
          >
            <template #activator="{ props }">
              <v-icon v-bind="props" size="18" color="error" class="ml-1"
                >mdi-alert</v-icon
              >
            </template>
          </v-tooltip>
        </v-btn>
        <div
          :style="{ backgroundColor: 'grey' }"
          class="ml-1 pl-1 pr-2 text-subtitle-1 font-weight-light text-white"
          v-if="uiData.textReise && !showReisekosten"
        >
          <v-row no-gutters class="w-full">
            <v-col>
              <span class="block ltext-label-medium">
                {{ uiData.textReise?.slice(0, 30) }}
                <span v-if="uiData.textReise?.length > 30">…</span>
                <br />

                <span class="text-body-small">{{ zusatzTextReise }}</span>
              </span>
            </v-col>

            <v-col v-if="anzahlReisen > 1" cols="auto" class="d-flex align-end">
              <v-tooltip
                text="mehrere Reisen für den gleichen Zeitraum erfasst"
                location="bottom"
              >
                <template #activator="{ props }">
                  <v-icon
                    v-bind="props"
                    variant="flat"
                    size="large"
                    color="error"
                    class="ml-1"
                    >mdi-alert</v-icon
                  >
                </template>
              </v-tooltip>
            </v-col>
          </v-row>
        </div>
        <v-spacer></v-spacer>

        <v-btn
          v-if="props.showTagessaldo"
          :color="uiData.colorZulagen"
          @click.prevent.stop="openFrame(openTagesSalden)"
          icon="mdi-information"
          variant="flat"
          size="small"
        >
        </v-btn>

        <v-btn
          v-if="
            props.showProjektstunden &&
            checkBegriff('WebWFProjektstunden') &&
            !uiData.sperre
          "
          color="grey"
          @click.prevent.stop="openFrame(openProjektstunden)"
          icon="mdi-wrench"
          variant="flat"
          size="small"
          class="ml-1"
        >
        </v-btn>
        <v-btn
          v-if="props.showManZulagen"
          :color="uiData.colorManZulagen"
          @click.prevent.stop="openFrame(openmanZulagen)"
          icon="mdi-clock-plus"
          variant="flat"
          size="small"
          class="ml-1"
        >
        </v-btn>
      </v-toolbar>
      <v-progress-linear
        color="warning"
        v-if="uiData.showLoading"
        indeterminate
      />
    </v-card-title>
    <v-dialog
      leave-absolute="true"
      id="warnung"
      max-width="350px"
      eager
      v-model="uiData.showDialogLocal"
      scrollable
    >
      <baseList
        :key="setCompKeyFehler"
        ref="warningList"
        :showClose="true"
        :showHeader="true"
        :dialog="true"
        v-if="uiData.editZeitbereich"
        @back="uiData.showDialogLocal = false"
        :zeitbereich="uiData.editZeitbereich"
        query="Web_Fehler_TimeLine"
        @dataloaded="warningListLoaded"
        :aic="pageParams.aic"
        :maxWidth="320"
      />
    </v-dialog>
    <div
      v-if="uiData.initialized && !menu"
      :style="`max-height: ${uiData.heightLocal - 120}px; overflow-y: auto;`"
    >
      <v-timeline align-start :dense="true" density="compact">
        <v-timeline-item
          v-for="(item, index) in uiData.items"
          :key="'c' + index"
          :dot-color="item.color"
          :icon="item.dot"
          size="small"
          fill-dot
          side="start"
          dense="true"
          style="padding-bottom: 5px"
          density="compact"
          class="no-gap"
        >
          <v-card
            :min-width="uiData.widthLocal - 70"
            :max-width="uiData.widthLocal - 70"
            class="pa-2"
          >
            <strong class="me-4"> {{ item.header }} {{ item.title }}</strong>
            <div class="caption">{{ item.title2 }}</div>
            <div class="caption">{{ item.subtitel }}</div>
            <div class="caption">{{ item.subtitel1 }}</div>
            <div class="caption" v-html="item.text"></div>
            <div v-if="item.auftrag" class="caption">
              {{ item.textAuftrag }}: {{ item.auftrag }}
            </div>
            <div v-if="item.taetigkeit" class="caption">
              {{ item.textTaetigkeit }}: {{ item.taetigkeit }}
            </div>
            <div v-if="item.kostenstelle" class="caption">
              {{ item.textKst }}: {{ item.kostenstelle }}
            </div>
            <div class="d-flex justify-end">
              <v-btn-group variant="outlined">
                <v-btn
                  v-if="
                    item.aic &&
                    (!item.aicAntrag || item.reise) &&
                    showNacherfassung &&
                    uiData.showEditDelete &&
                    !item.bde &&
                    !item.sperre &&
                    !uiData.sperre
                  "
                  icon="mdi-pencil"
                  variant="text"
                  size="small"
                  @click.prevent.stop="openEditStempelungen(item.aic)"
                >
                </v-btn>
                <v-btn
                  v-if="
                    (item.bde &&
                      !uiData.bdeAntrag &&
                      !item.aicAntrag &&
                      !item.aicReise &&
                      showNacherfassung &&
                      checkBegriff('Web_NacherfassungSoftTerm') &&
                      !uiData.sperre) ||
                    (!item.aic &&
                      item.bde &&
                      !item.aicAntrag &&
                      showNacherfassung &&
                      checkBegriff('Web_NacherfassungSoftTerm') &&
                      !uiData.sperre)
                  "
                  icon="mdi-pencil"
                  variant="text"
                  size="small"
                  @click.prevent.stop="openEditStempelungenBDE(item.aic)"
                >
                </v-btn>
                <v-btn
                  v-if="
                    (item.bde &&
                      !uiData.bdeAntrag &&
                      !item.aicAntrag &&
                      !item.aicReise &&
                      showNacherfassung &&
                      uiData.showEditDelete) ||
                    (item.aic &&
                      !item.bde &&
                      !item.aicAntrag &&
                      !item.aicReise &&
                      showNacherfassung &&
                      uiData.showEditDelete &&
                      !item.sperre &&
                      !uiData.sperre)
                  "
                  icon="mdi-delete"
                  variant="text"
                  size="small"
                  @click="bewDelete(item.aic)"
                >
                </v-btn>
                <v-btn
                  v-if="
                    item.aicAntrag &&
                    !item.bewilligtAntrag &&
                    !props.freigabeAntrag
                  "
                  icon="mdi-delete"
                  variant="text"
                  size="small"
                  @click="
                    bewDeleteAntrag(
                      item.aicAntrag,
                      item.aic,
                      false,
                      item.antrag,
                    )
                  "
                  :disabled="item.noDelete"
                >
                </v-btn>
                <v-btn
                  v-if="
                    item.bde &&
                    uiData.bdeAntrag &&
                    !item.aicAntrag &&
                    !item.aicReise &&
                    item.aic &&
                    showNacherfassung &&
                    !uiData.sperre &&
                    checkBegriff('Web_StempelantragNeuBDE')
                  "
                  @click="
                    calcFunction(
                      'Web_StempelantragNeuBDE',
                      item.aic,
                      false,
                      true,
                    )
                  "
                  icon="mdi-shield-check"
                  variant="text"
                  size="small"
                  :zeitbereich="uiData.editZeitbereich"
                >
                </v-btn>
                <v-btn
                  v-if="
                    item.aic &&
                    !item.bde &&
                    !item.aicAntrag &&
                    showNacherfassung &&
                    !item.sperre &&
                    checkBegriff('Web_ST_Antrag_erzeugen')
                  "
                  @click="
                    calcFunction(
                      'Web_ST_Antrag_erzeugen',
                      item.aic,
                      false,
                      true,
                    )
                  "
                  icon="mdi-shield-check"
                  variant="text"
                  size="small"
                  :zeitbereich="uiData.editZeitbereich"
                >
                </v-btn>
                <v-btn
                  v-if="
                    item.aicAntrag &&
                    (!item.bewilligtAntrag || showName) &&
                    !item.sperre &&
                    checkBegriff('Web_editStempelantrag')
                  "
                  icon="mdi-shield-check"
                  variant="text"
                  size="small"
                  @click.prevent.stop="
                    openEditStempelantrag(
                      item.aicAntrag,
                      item.bewilligtAntrag,
                      item.abgelehntAntrag,
                      true,
                      item.bde,
                      false,
                      item.reise,
                      item.noDelete,
                      item.aic,
                    )
                  "
                >
                </v-btn>
                <v-btn
                  variant="text"
                  size="small"
                  icon="mdi-airplane"
                  v-if="
                    item.reise &&
                    item.aicReise &&
                    checkBegriff('Web_Reisekosten') &&
                    !item.sperre
                  "
                  @click.prevent.stop="
                    openEditReise(
                      item.aicReise,
                      item.reiseName,
                      item.aic,
                      item.aicAntrag,
                    )
                  "
                >
                </v-btn>
                <v-btn
                  v-if="
                    item.reise &&
                    item.aicReise &&
                    !item.aicAntrag &&
                    checkBegriff('Web_Reisekosten') &&
                    !item.sperre &&
                    ((uiData.antragMoeglich && !item.aic) ||
                      !uiData.antragMoeglich)
                  "
                  icon="mdi-delete"
                  variant="text"
                  size="small"
                  @click="
                    bewDeleteAntrag(
                      item.aicReise,
                      item.aic,
                      true,
                      item.antrag,
                      item,
                    )
                  "
                  :disabled="item.noDelete"
                >
                </v-btn>
                <v-menu
                  open-on-click
                  top
                  offset-y
                  v-if="item.Abflatlong && props.freigabeAntrag"
                  v-model="item.mapVisible"
                  :close-on-click="true"
                  :close-on-content-click="false"
                >
                  <template v-slot:activator="{ props }">
                    <v-btn
                      variant="text"
                      icon="mdi-map-marker-outline"
                      size="small"
                      v-bind="props"
                    >
                    </v-btn>
                  </template>

                  <EditGps
                    :standort="item.Abflatlong"
                    v-if="item.mapVisible"
                  ></EditGps>
                </v-menu>
              </v-btn-group>
            </div>
          </v-card>
        </v-timeline-item>
      </v-timeline>
    </div>
    <v-sheet>
      <RkMitarbeiterTemplateAnzeigeReiseTimeLine
        :key="setCompKey"
        :zeitbereich="uiData.editZeitbereich"
        :aic="aic"
        v-if="uiData.reisen && uiData.showReisekostenMutable"
        ref="reisen"
        :width="uiData.widthLocal"
        :admin="props.admin"
        :heightLocal="uiData.heightLocal - 310"
        @reisename="updateReiseName"
        @freigabeReise="onDialogClose"
        :showReise="menu"
      ></RkMitarbeiterTemplateAnzeigeReiseTimeLine>
    </v-sheet>
    <v-fab-transition v-if="props.showClose">
      <CommonBaseButtons
        v-show="!uiData.hidden"
        :createVisible="false"
        :refreshVisible="false"
        :deleteVisible="false"
        :saveVisible="false"
        @back="goBack()"
        backColor="pink"
        :showBackText="false"
        backIcon="mdi-close"
        :darkBtn="true"
        top
        left
        absoulte
        class="v-btn--example"
      ></CommonBaseButtons>
    </v-fab-transition>
  </v-card>
</template>

<style scoped>
.scroll {
  overflow-y: auto;
}
.timeCard {
  min-width: 200px;
  padding-right: 15px;
  max-width: 220px;
}
.cardText {
  padding: 0px;
  margin-bottom: 5px;
  text-align: left;
}
#lateral .v-btn--example {
  bottom: 0;
  position: absolute;
  padding: 0 0 -20 -20;
}
@media (max-width: 600px) {
  :deep(
    .v-timeline.v-timeline--vertical .v-timeline-item .v-timeline-item__body
  ) {
    padding-inline-start: 6px !important; /* leicht eingerückt */
  }
}
</style>
